import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Scales, Factory, ClipboardText, Plus, Trash, FileText,
  CheckCircle, Circle, Eye, DownloadSimple, User as UserIcon, Calendar,
  Archive, Folder, CaretRight, CaretDown, UploadSimple, X
} from "@phosphor-icons/react";
import { DocumentViewerModal } from "../components/DocumentViewer";

export default function ObligationDetail({ embeddedId, onBack }) {
  const params = useParams();
  const id = embeddedId || params.id;
  const { user, can } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(() => (!embeddedId && new URLSearchParams(window.location.search).get("tab") === "documents") ? "documents" : "info");
  const [showActForm, setShowActForm] = useState(false);
  const [showZipUpload, setShowZipUpload] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [collapsedFolders, setCollapsedFolders] = useState({});

  const reload = () => api.get(`/obligations/${id}/detail`).then(r => setData(r.data));
  useEffect(() => { reload(); }, [id]);

  const canEdit = can("obligations", "write");
  const canDelete = can("obligations", "delete");

  const toggleActivity = async (aid) => {
    try { await api.patch(`/activities/${aid}/toggle`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteActivity = async (aid) => {
    if (!window.confirm("¿Eliminar actividad?")) return;
    try { await api.delete(`/activities/${aid}`); toast.success("Eliminada"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!data) return <div className="text-center py-16 text-neutral-400">Cargando...</div>;
  const { obligation: o, installation, activities, documents } = data;
  const completed = activities.filter(a => a.completed).length;
  const total = activities.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="obligation-detail-page">
      {onBack
        ? <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900" data-testid="obligation-back-btn"><ArrowLeft size={16}/> Volver</button>
        : <Link to="/obligaciones" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900"><ArrowLeft size={16}/> Volver a obligaciones</Link>
      }

      <div className="bg-white border border-neutral-200 rounded-md p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-emerald-50 text-emerald-700 rounded-md flex items-center justify-center border border-emerald-100"><Scales size={26} weight="duotone"/></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Cumplimiento</div>
              <h1 className="font-display text-3xl font-black tracking-tighter mt-1">{o.name}</h1>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-semibold">{o.authority}</span>
                {o.category && <span className="px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded text-xs">{o.category}</span>}
                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-semibold">{o.frequency}</span>
              </div>
            </div>
          </div>
        </div>

        {installation && (
          <div className="mt-4 flex items-center gap-3 p-3 bg-neutral-50 rounded-md text-sm">
            <Factory size={18} className="text-indigo-700" weight="duotone"/>
            <div><span className="text-neutral-500">Instalación:</span> <span className="font-semibold">{installation.name}</span> {installation.type && <span className="text-neutral-500">· {installation.type}</span>}</div>
          </div>
        )}
        {o.responsible && <div className="mt-3 text-sm flex items-center gap-2 text-neutral-600"><UserIcon size={14}/> Responsable: <span className="font-semibold text-neutral-900">{o.responsible}</span></div>}
        {o.description && <div className="mt-4 pt-4 border-t border-neutral-200 text-sm text-neutral-700">{o.description}</div>}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200">
        {[
          {k:"info", l:"Información", n:null},
          {k:"activities", l:"Actividades", n:`${completed}/${total}`},
          {k:"documents", l:"Documentos", n:documents.length},
        ].map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 ${tab===t.k?'border-blue-600 text-blue-700':'border-transparent text-neutral-500 hover:text-neutral-900'}`}
            data-testid={`tab-${t.k}`}>
            {t.l} {t.n!=null && <span className="ml-1 text-xs bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded">{t.n}</span>}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <div className="bg-white border border-neutral-200 rounded-md p-6 grid md:grid-cols-2 gap-4 text-sm" data-testid="info-tab">
          <Info label="Autoridad" value={o.authority}/>
          <Info label="Categoría" value={o.category||"—"}/>
          <Info label="Frecuencia" value={o.frequency}/>
          <Info label="Responsable" value={o.responsible||"—"}/>
          <Info label="Instalación" value={installation?.name||"Empresa general"}/>
          <Info label="Creada" value={o.created_at?.slice(0,10)}/>
        </div>
      )}

      {tab === "activities" && (
        <div className="bg-white border border-neutral-200 rounded-md p-6" data-testid="activities-tab">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Checklist de actividades</h2>
              <p className="text-sm text-neutral-500">{completed}/{total} completadas · {pct}%</p>
            </div>
            {canEdit && (
              <button onClick={()=>setShowActForm(true)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md" data-testid="new-activity-btn">
                <Plus size={14} weight="bold"/> Nueva actividad
              </button>
            )}
          </div>

          {total > 0 && (
            <div className="h-2 bg-neutral-100 rounded-full overflow-hidden mb-5">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all duration-700" style={{width:`${pct}%`}}></div>
            </div>
          )}

          {activities.length === 0 ? (
            <div className="py-12 text-center text-neutral-400 border border-dashed border-neutral-300 rounded-md">
              <ClipboardText size={42} className="mx-auto mb-2 text-neutral-300" weight="duotone"/>
              <p className="text-sm">Sin actividades. {canEdit && "Agrega la primera para iniciar el seguimiento."}</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="activities-list">
              {activities.map(a => (
                <li key={a.id} className={`group flex items-start gap-3 p-3 border rounded-md transition-all ${a.completed?'bg-emerald-50/40 border-emerald-200':'bg-white border-neutral-200 hover:border-neutral-300'}`} data-testid={`activity-${a.id}`}>
                  <button onClick={()=>canEdit && toggleActivity(a.id)} disabled={!canEdit} className="flex-shrink-0 mt-0.5 disabled:cursor-not-allowed" data-testid={`activity-check-${a.id}`}>
                    {a.completed ? <CheckCircle size={22} weight="fill" className="text-emerald-600"/> : <Circle size={22} className="text-neutral-300 hover:text-blue-500 transition-colors"/>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold ${a.completed?'text-neutral-500 line-through':'text-neutral-900'}`}>{a.title}</div>
                    {a.description && <div className="text-sm text-neutral-600 mt-0.5">{a.description}</div>}
                    <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-neutral-500">
                      {a.due_date && <span className="inline-flex items-center gap-1"><Calendar size={12}/> {a.due_date}</span>}
                      {a.responsible && <span className="inline-flex items-center gap-1"><UserIcon size={12}/> {a.responsible}</span>}
                      {a.completed && a.completed_by_name && <span className="text-emerald-700">Completada por {a.completed_by_name}</span>}
                    </div>
                  </div>
                  {canDelete && (
                    <button onClick={()=>deleteActivity(a.id)} className="opacity-0 group-hover:opacity-100 text-rose-400 hover:text-rose-600 transition-opacity flex-shrink-0" data-testid={`delete-activity-${a.id}`}>
                      <Trash size={16}/>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "documents" && (
        <div className="bg-white border border-neutral-200 rounded-md p-6" data-testid="documents-tab">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display text-xl font-bold tracking-tight">Documentos vinculados</h2>
            {can("documents", "write") && (
              <button
                onClick={()=>setShowZipUpload(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded-md text-sm"
                data-testid="import-zip-btn"
                title="Cargar múltiples documentos manteniendo estructura de carpetas"
              >
                <Archive size={16} weight="duotone"/> Importar ZIP
              </button>
            )}
          </div>
          {documents.length === 0 ? (
            <div className="py-12 text-center text-neutral-400 border border-dashed border-neutral-300 rounded-md">
              <FileText size={42} className="mx-auto mb-2 text-neutral-300" weight="duotone"/>
              <p className="text-sm">Sin documentos vinculados. Sube documentos individuales o importa un ZIP con estructura de carpetas.</p>
            </div>
          ) : (
            (() => {
              // Agrupar por folder_path (raíz = "")
              const groups = {};
              for (const d of documents) {
                const k = d.folder_path || "";
                if (!groups[k]) groups[k] = [];
                groups[k].push(d);
              }
              const folders = Object.keys(groups).sort((a,b)=>a.localeCompare(b));
              return (
                <div className="space-y-2" data-testid="docs-folder-tree">
                  {folders.map(folder => {
                    const isRoot = folder === "";
                    const isCollapsed = !!collapsedFolders[folder];
                    return (
                      <div key={folder || "_root_"} className="border border-neutral-200 rounded-md overflow-hidden">
                        {!isRoot && (
                          <button
                            type="button"
                            onClick={()=>setCollapsedFolders(prev=>({ ...prev, [folder]: !prev[folder] }))}
                            className="w-full flex items-center gap-2 px-3 py-2 bg-neutral-50 hover:bg-neutral-100 border-b border-neutral-100 text-left"
                            data-testid={`folder-toggle-${folder}`}
                          >
                            {isCollapsed ? <CaretRight size={14} weight="bold"/> : <CaretDown size={14} weight="bold" className="text-blue-600"/>}
                            <Folder size={16} weight="duotone" className="text-amber-500"/>
                            <span className="font-semibold text-sm text-neutral-800 flex-1">{folder}</span>
                            <span className="text-[11px] text-neutral-500">{groups[folder].length} archivo(s)</span>
                          </button>
                        )}
                        {(isRoot || !isCollapsed) && (
                          <ul className="divide-y divide-neutral-100" data-testid={`folder-docs-${folder||'root'}`}>
                            {groups[folder].map(d => (
                              <li key={d.id} className="py-3 px-4 flex items-center gap-3" data-testid={`obl-doc-${d.id}`}>
                                <div className="w-9 h-9 bg-blue-50 text-blue-700 rounded-md flex items-center justify-center border border-blue-100"><FileText size={16} weight="duotone"/></div>
                                <div className="flex-1 min-w-0">
                                  <Link to={`/documentos/${d.id}`} className="font-semibold text-neutral-900 hover:text-blue-700 truncate block">{d.name}</Link>
                                  <div className="text-xs text-neutral-500 flex flex-wrap gap-2 mt-0.5">
                                    <span>{d.period}</span>
                                    <span>·</span>
                                    <span>{d.authority}</span>
                                    {d.status === "vigente" && <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">Vigente</span>}
                                    {d.status === "proximo" && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">Próximo {d.days_to_expire}d</span>}
                                    {d.status === "vencido" && <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded">Vencido</span>}
                                    {d.approval_status === "aprobado" && <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">Aprobado</span>}
                                    {d.approval_status === "rechazado" && <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded">Rechazado</span>}
                                    {(!d.approval_status || d.approval_status === "pendiente") && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">Aprobación pendiente</span>}
                                  </div>
                                </div>
                                <button onClick={()=>setViewDoc(d)} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-semibold" data-testid={`obl-preview-${d.id}`}><Eye size={14}/> Ver</button>
                                <a href={`/api/documents/file/${d.drive_file_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-semibold"><DownloadSimple size={14}/></a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}
        </div>
      )}

      {showActForm && <ActivityForm obligationId={id} onClose={()=>setShowActForm(false)} onSuccess={()=>{setShowActForm(false); reload();}}/>}
      {showZipUpload && <ZipUploadModal obligation={o} onClose={()=>setShowZipUpload(false)} onSuccess={()=>{ setShowZipUpload(false); reload(); }}/>}
      {viewDoc && <DocumentViewerModal docId={viewDoc.id} fileId={viewDoc.drive_file_id} mimeType={viewDoc.mime_type} filename={viewDoc.filename} onClose={()=>setViewDoc(null)}/>}
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">{label}</div><div className="font-semibold text-neutral-900">{value}</div></div>;
}

function ZipUploadModal({ obligation, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [period, setPeriod] = useState(new Date().getFullYear());
  const [responsible, setResponsible] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onSelect = (f) => {
    if (!f) return;
    if (!/\.zip$/i.test(f.name)) { toast.error("Selecciona un archivo .zip"); return; }
    setFile(f);
  };

  const upload = async () => {
    if (!file) { toast.error("Selecciona un archivo ZIP"); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("period", period);
      fd.append("authority", obligation.authority || "");
      fd.append("genre", "obligacion");
      if (obligation.installation_id) fd.append("installation_id", obligation.installation_id);
      fd.append("obligation_id", obligation.id);
      fd.append("responsible", responsible || "");
      fd.append("zip_file", file);
      const r = await api.post("/documents/bulk-zip", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`ZIP procesado: ${r.data.created} archivo(s) importados`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al procesar el ZIP");
    } finally { setBusy(false); }
  };

  const finish = () => { onSuccess?.(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="zip-upload-modal">
      <div className="bg-white rounded-md w-full max-w-xl border border-neutral-200 max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <div>
            <h2 className="font-display text-lg font-bold">Importar ZIP</h2>
            <p className="text-xs text-neutral-500 mt-0.5">La estructura de carpetas del ZIP se preservará y cada archivo se registrará como documento independiente.</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {!result && (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Archivo ZIP</label>
                <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-neutral-300 hover:border-blue-500 rounded-md cursor-pointer transition-colors">
                  <UploadSimple size={32} weight="duotone" className="text-blue-600"/>
                  <div className="text-sm text-neutral-700 font-semibold">{file ? file.name : "Seleccionar archivo .zip"}</div>
                  {file && <div className="text-xs text-neutral-500">{(file.size/1024/1024).toFixed(2)} MB</div>}
                  <input type="file" accept=".zip,application/zip" className="hidden" onChange={(e)=>onSelect(e.target.files?.[0])} data-testid="zip-file-input"/>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Periodo</label>
                  <input type="number" value={period} onChange={e=>setPeriod(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="zip-period"/>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Responsable (opcional)</label>
                  <input value={responsible} onChange={e=>setResponsible(e.target.value)} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="zip-responsible"/>
                </div>
              </div>
              <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-md p-3">
                <p className="font-semibold text-neutral-700 mb-1">Cada archivo del ZIP será un documento independiente</p>
                <p>Podrás editar por separado: vigencia, vencimiento, responsable, aprobador, versionado y tareas programadas.</p>
              </div>
            </>
          )}
          {result && (
            <div className="space-y-3" data-testid="zip-result">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                <div className="font-semibold text-emerald-800">Procesado: {result.zip_name}</div>
                <div className="text-sm text-emerald-700 mt-1">
                  <strong>{result.created}</strong> documento(s) creado(s){result.errors ? `, ${result.errors} error(es)` : ""}.
                </div>
              </div>
              {result.documents?.length > 0 && (
                <div className="border border-neutral-200 rounded-md max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-neutral-100 bg-neutral-50 text-neutral-500 uppercase tracking-widest">
                      <th className="px-3 py-2 text-left font-semibold">Carpeta</th>
                      <th className="px-3 py-2 text-left font-semibold">Archivo</th>
                    </tr></thead>
                    <tbody>
                      {result.documents.map(d => (
                        <tr key={d.id} className="border-b border-neutral-100">
                          <td className="px-3 py-1.5 text-neutral-500">{d.folder_path || <em className="text-neutral-300">raíz</em>}</td>
                          <td className="px-3 py-1.5 font-medium">{d.filename}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {result.error_detail?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-rose-600 font-semibold">Ver errores ({result.error_detail.length})</summary>
                  <ul className="mt-2 space-y-1">
                    {result.error_detail.map((e, i) => <li key={i} className="text-neutral-600">{e.file}: {e.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 bg-neutral-50">
          {!result ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md">Cancelar</button>
              <button onClick={upload} disabled={busy || !file} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="zip-upload-submit">
                <Archive size={14}/> {busy ? "Procesando..." : "Importar"}
              </button>
            </>
          ) : (
            <button onClick={finish} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md" data-testid="zip-finish-btn">Cerrar</button>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityForm({ obligationId, onClose, onSuccess }) {
  const [f, setF] = useState({ title:"", description:"", due_date:"", responsible:"" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title) return toast.error("Título requerido");
    setBusy(true);
    try {
      await api.post(`/obligations/${obligationId}/activities`, f);
      toast.success("Actividad creada"); onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail||"Error"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="activity-modal">
      <div className="bg-white rounded-md w-full max-w-lg border border-neutral-200">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-lg font-bold">Nueva actividad</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700">✕</button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Título</label>
            <input value={f.title} onChange={e=>setF({...f,title:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="activity-title"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={f.description} onChange={e=>setF({...f,description:e.target.value})} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md" data-testid="activity-desc"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Fecha límite</label>
              <input type="date" value={f.due_date} onChange={e=>setF({...f,due_date:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="activity-due"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Responsable</label>
              <input value={f.responsible} onChange={e=>setF({...f,responsible:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="activity-resp"/>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="activity-submit">{busy?"Guardando...":"Guardar"}</button>
        </div>
      </div>
    </div>
  );
}
